// EDEN · BOOKS — the department's books as a library, one grade at a time (T1 review, owner 9 Oct 2026).
//
// "Our teachers find it hard to use the website compared to their traditional working style — just open the
// books." This tab is that habit, online: pick the grade, pick the book, open any part of it. Every book shows
// what it carries — its audio, its answer key, the Word files of its worksheets — so nothing is hidden behind
// a lesson row any more. The pages, sounds and files stay behind the signed-in /m/ gate; this screen only
// lists them (curriculum/library.json, generated by scripts/build-library.mjs).
//
//   /books?grade=6     the grade's shelves: coursebook, workbook, practice, grammar, readers, exam practice
//   /books/<id>        one book: open the whole book, its contents one tap each, all its audio, its answers

import { api } from "../api";
import { esc } from "../dom";
import { icon } from "../icons";
import { t } from "../i18n";
import { pageHeader, emptyState } from "../ui";
import { gradeFromClass } from "../curriculum";
import { byGrade, type ClassRow } from "../lesson-data";
import { selectedClassId } from "./roadmap";
import { registerTarget, printedRange, expandPages } from "../book-reader";
import { contentTarget, fileHref, hasAnswers, loadLibrary, wholeBookTarget, type LibBook, type LibContent, type LibraryFile } from "../library";
import { navigate } from "../router";

const SECTION_ICON: Record<string, Parameters<typeof icon>[0]> = {
  course: "book", workbook: "pencil", practice: "sheet", grammar: "puzzle", reading: "eye", phonics: "audio", exam: "tests", teacher: "check",
};

/** The grade to open: ?grade= wins, then the class picked elsewhere in EDEN, then the teacher's first class. */
async function startGrade(query: URLSearchParams): Promise<number> {
  const q = Number(query.get("grade"));
  if (q >= 1 && q <= 10) return q;
  try {
    const { classes } = await api<{ classes: ClassRow[] }>("/api/classes?scope=own");
    const own = [...classes].sort(byGrade);
    const picked = own.find((c) => c.id === selectedClassId()) ?? own[0];
    const g = picked ? gradeFromClass(picked) : null;
    if (g && g >= 1 && g <= 10) return g;
  } catch { /* offline or signed out: fall through */ }
  return 1;
}

function gradePicker(grade: number): string {
  return `<label class="inline-field"><span class="sr-only">Grade</span><select id="bk-grade" aria-label="Grade">${
    Array.from({ length: 10 }, (_, i) => i + 1).map((g) => `<option value="${g}"${g === grade ? " selected" : ""}>Grade ${g}</option>`).join("")
  }</select></label>`;
}

/** The small facts under a book's name: pages · audio · answers · Word files. */
function facts(b: LibBook, lib: LibraryFile): string {
  const out = [`<span class="lib-fact">${b.pages} pages</span>`];
  if (b.tracks?.length) out.push(`<span class="lib-fact">${icon("audio", "ico-sm")}${b.tracks.length} ${b.tracks.length === 1 ? "track" : "tracks"}</span>`);
  const keyed = b.contents?.some((c) => hasAnswers(b, c, lib)) || (b.answers && lib.books[b.answers]);
  if (keyed) out.push(`<span class="lib-fact">${icon("check", "ico-sm")}Answers</span>`);
  if (b.downloads) out.push(`<span class="lib-fact">${icon("download", "ico-sm")}Word files</span>`);
  return out.join("");
}

function tile(b: LibBook): string {
  const n = /(\d+)(?!.*\d)/.exec(b.title)?.[1] ?? "";
  return `<span class="lib-tile lib-${esc(b.section)}" aria-hidden="true">${icon(SECTION_ICON[b.section] ?? "book")}${n ? `<b>${esc(n)}</b>` : ""}</span>`;
}

function bookCard(id: string, b: LibBook, lib: LibraryFile, grade: number): string {
  const search = [b.title, ...(b.contents ?? []).map((c) => c.label)].join(" ").toLowerCase();
  return `<a class="lib-book" data-link href="/books/${encodeURIComponent(id)}?grade=${grade}" data-lib-search="${esc(search)}">
    ${tile(b)}
    <span class="lib-book-text"><b>${esc(b.title)}</b><span class="lib-facts">${facts(b, lib)}</span></span>
    ${icon("chevron", "lib-go")}
  </a>`;
}

export async function renderBooks(page: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const [lib, grade] = await Promise.all([loadLibrary(), startGrade(query)]);
  const head = pageHeader({
    title: t("nav.books"),
    sub: `Every book for Grade ${grade}. Open any page, play the audio, find the answers.`,
    actions: gradePicker(grade),
  });
  if (!lib) {
    page.innerHTML = `${head}${emptyState("The book list did not load. Check the connection and open Books again.")}`;
    wireGrade(page);
    return;
  }
  const shelves = lib.grades[String(grade)] ?? [];
  page.innerHTML = `${head}
    <div class="lib-find">
      <label class="field"><span class="sr-only">Find a book or a unit</span>
        <input type="search" id="lib-q" placeholder="Find a book, a unit or a story" autocomplete="off" /></label>
    </div>
    ${shelves.length ? shelves.map((s) => `<section class="lib-shelf" data-lib-shelf>
      <h2 class="lib-shelf-title">${icon(SECTION_ICON[s.key] ?? "book")}<span>${esc(s.title)}</span></h2>
      ${s.hint ? `<p class="lib-shelf-hint">${esc(s.hint)}</p>` : ""}
      <div class="lib-books">${s.ids.filter((id) => lib.books[id]).map((id) => bookCard(id, lib.books[id]!, lib, grade)).join("")}</div>
    </section>`).join("") : emptyState(`No books are online for Grade ${grade} yet.`)}
    <p class="lib-none" id="lib-none" hidden>No book or unit on this grade's shelves has that name.</p>`;
  wireGrade(page);
  const input = page.querySelector<HTMLInputElement>("#lib-q");
  input?.addEventListener("input", () => {
    const words = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0;
    page.querySelectorAll<HTMLElement>("[data-lib-shelf]").forEach((shelf) => {
      let inShelf = 0;
      shelf.querySelectorAll<HTMLElement>("[data-lib-search]").forEach((a) => {
        const hit = words.every((w) => (a.dataset.libSearch ?? "").includes(w));
        a.hidden = !hit;
        if (hit) inShelf++;
      });
      shelf.hidden = !inShelf;
      shown += inShelf;
    });
    page.querySelector<HTMLElement>("#lib-none")!.hidden = shown > 0;
  });
}

function wireGrade(page: HTMLElement): void {
  (page.querySelector("#bk-grade") as HTMLSelectElement | null)?.addEventListener("change", (e: Event) => {
    navigate(`/books?grade=${(e.target as HTMLSelectElement).value}`);
  });
}

function pagesWords(c: { pdf: [number, number, number][] }): string {
  const r = printedRange(c.pdf);
  if (r) return r.includes("–") ? `pages ${r}` : `page ${r}`;
  const n = expandPages(c.pdf).length;
  return `${n} ${n === 1 ? "page" : "pages"}`;
}

function contentRow(id: string, b: LibBook, c: LibContent, lib: LibraryFile): string {
  const tg = contentTarget(id, b, c, lib);
  const chips = [
    tg.tracks?.length ? `<span class="lib-fact">${icon("audio", "ico-sm")}${tg.tracks.length === 1 ? esc(tg.tracks[0]!.label) : `${tg.tracks.length} tracks`}</span>` : "",
    hasAnswers(b, c, lib) ? `<span class="lib-fact">${icon("check", "ico-sm")}Answers</span>` : c.key_none ? `<span class="lib-fact">${esc(c.key_none)}</span>` : "",
  ].join("");
  const files = [
    c.file ? `<a class="bk-file" href="${esc(fileHref(id, c.file))}" download>${icon("download", "ico-sm")}<span>Word file</span></a>` : "",
    c.key_file ? `<a class="bk-file" href="${esc(fileHref(id, c.key_file))}" download>${icon("download", "ico-sm")}<span>Answer key (Word)</span></a>` : "",
  ].join("");
  return `<li class="lib-part" data-lib-search="${esc(c.label.toLowerCase())}">
    <button type="button" class="lib-part-open" data-book-open="${registerTarget(tg)}">
      <span class="lib-part-label">${esc(c.label)}</span><span class="lib-part-pages">${esc(pagesWords(c))}</span>
    </button>
    ${chips || files ? `<div class="lib-part-more">${chips}${files}</div>` : ""}
  </li>`;
}

export async function renderBook(page: HTMLElement, params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const lib = await loadLibrary();
  const id = params.id ?? "";
  const b = lib?.books[id];
  const grade = Number(query.get("grade")) || null;
  const back = `/books${grade ? `?grade=${grade}` : ""}`;
  if (!lib || !b) {
    page.innerHTML = `${pageHeader({ title: t("nav.books"), crumbs: [{ label: t("nav.books"), href: back }, { label: "Book" }] })}
      ${emptyState(lib ? "This book is not on the shelf." : "The book list did not load. Check the connection and try again.", `<a class="btn secondary" data-link href="${esc(back)}">Back to Books</a>`)}`;
    return;
  }
  const answers = b.answers ? lib.books[b.answers] : undefined;
  const guide = b.guide ? lib.books[b.guide] : undefined;
  const whole = wholeBookTarget(id, b, lib);
  const actions = [
    `<button type="button" class="btn" data-book-open="${registerTarget(whole)}">${icon("book", "ico-sm")}<span>Open the book</span></button>`,
    answers ? `<button type="button" class="btn secondary" data-book-open="${registerTarget({ ...wholeBookTarget(b.answers!, answers, lib), teacherOnly: true })}">${icon("check", "ico-sm")}<span>Answers</span></button>` : "",
    guide ? `<button type="button" class="btn secondary" data-book-open="${registerTarget({ ...wholeBookTarget(b.guide!, guide, lib), teacherOnly: true })}">${icon("pack", "ico-sm")}<span>Teacher’s guide</span></button>` : "",
  ].join("");
  const contents = b.contents ?? [];
  const audio = b.tracks?.length
    ? `<section class="card lib-audio">
        <h2 class="card-title">${icon("audio")}<span>All the audio · ${b.tracks.length} ${b.tracks.length === 1 ? "track" : "tracks"}</span></h2>
        <p class="lib-shelf-hint">The page prints the track number next to the activity. Pick it and press play.</p>
        <div class="lib-player">
          <label class="field"><span class="sr-only">Track</span><select data-lib-track><option value="" disabled selected>Pick the track number on the page</option>${b.tracks.map((tr) => `<option value="${esc(tr.id)}">${esc(tr.label)}</option>`).join("")}</select></label>
          <audio controls preload="none" data-lib-audio></audio>
        </div>
        <p class="lib-audio-msg" role="status" hidden></p>
      </section>`
    : "";
  page.innerHTML = `${pageHeader({
    crumbs: [{ label: t("nav.books"), href: back }, { label: grade ? `Grade ${grade}` : "All books", href: back }, { label: b.title }],
    title: b.title,
    sub: [`${b.pages} pages`, b.note ? esc(b.note) : ""].filter(Boolean).join(" · "),
    actions,
  })}
    <div class="lib-detail">
      ${contents.length ? `<section class="card lib-contents">
        <h2 class="card-title">${icon("plans")}<span>Contents</span></h2>
        ${contents.length > 12 ? `<label class="field lib-find"><span class="sr-only">Find in this book</span><input type="search" id="lib-q" placeholder="Find a unit, a lesson or a test" autocomplete="off" /></label>` : ""}
        <ol class="lib-parts">${contents.map((c) => contentRow(id, b, c, lib)).join("")}</ol>
      </section>` : ""}
      ${audio}
    </div>`;

  const input = page.querySelector<HTMLInputElement>("#lib-q");
  input?.addEventListener("input", () => {
    const words = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    page.querySelectorAll<HTMLElement>(".lib-part").forEach((li) => {
      li.hidden = !words.every((w) => (li.dataset.libSearch ?? "").includes(w));
    });
  });
  const pick = page.querySelector("[data-lib-track]") as HTMLSelectElement | null;
  const player = page.querySelector<HTMLAudioElement>("[data-lib-audio]");
  const msg = page.querySelector<HTMLElement>(".lib-audio-msg");
  if (pick && player && msg) {
    pick.addEventListener("change", () => {
      if (!pick.value) return;
      msg.hidden = true;
      player.src = `/m/${encodeURIComponent(id)}/audio/${encodeURIComponent(pick.value)}`;
      void player.play().catch(() => {});
    });
    player.addEventListener("error", async () => {
      if (!player.getAttribute("src")) return;
      let status = 0;
      try { status = (await fetch(player.src, { method: "HEAD", cache: "no-store" })).status; } catch { /* offline */ }
      msg.hidden = false;
      msg.textContent = status === 401 ? "Your sign-in has ended. Sign in again to play audio."
        : status === 404 ? "This track is not online yet." : "The track did not play. Check the connection and try again.";
    });
  }
}
